import { rem } from 'polished'
import * as React from 'react'
import styled from 'styled-components'
import Image from 'components/Common/Image'

/**
 * LuxKit-Web "Media Ratios - Drop": 1:1 frame with a small radius on one corner.
 * Paths are in objectBoundingBox units so they scale with the frame.
 */
const DROP_CLIP_PATHS = {
  'bottom-right':
    'M0 0.5C0 0.223858 0.223858 0 0.5 0C0.776142 0 1 0.223858 1 0.5V0.969754C1 0.986569 0.986569 1 0.969754 1H0.5C0.223858 1 0 0.776142 0 0.5Z',
  'bottom-left':
    'M1 0.5C1 0.223858 0.776142 0 0.5 0C0.223858 0 0 0.223858 0 0.5V0.969754C0 0.986569 0.013431 1 0.030246 1H0.5C0.776142 1 1 0.776142 1 0.5Z',
  'top-right':
    'M0 0.5C0 0.776142 0.223858 1 0.5 1C0.776142 1 1 0.776142 1 0.5V0.030246C1 0.013431 0.986569 0 0.969754 0H0.5C0.223858 0 0 0.223858 0 0.5Z',
  'top-left':
    'M1 0.5C1 0.776142 0.776142 1 0.5 1C0.223858 1 0 0.776142 0 0.5V0.030246C0 0.013431 0.013431 0 0.030246 0H0.5C0.776142 0 1 0.223858 1 0.5Z',
} as const

export type DropPosition = keyof typeof DROP_CLIP_PATHS

const DEFAULT_SIZE = 406

const DropShell = styled.div`
  position: relative;
  flex-shrink: 0;
  width: var(--drop-size, ${rem(DEFAULT_SIZE)});
  max-width: 100%;
`

const ClipSvg = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
`

const DropRoot = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  clip-path: var(--drop-clip-path);

  > * {
    display: block;
    width: 100%;
    height: 100%;
  }
`

interface Props {
  className?: string
  /**
   * Figma "Point Position": which corner has the small drop radius.
   * @default top-left
   */
  position?: DropPosition
  /**
   * Frame size in pixels. Height follows from the 1:1 aspect ratio.
   * @default 406
   */
  size?: number
  /**
   * Luxury Escapes image id (not a DOM `id`). Same as `Image` / `ImageCarousel`.
   * Preferred over `image.url` when both are provided.
   * For a host element id, wrap Drop instead of passing it here.
   */
  id?: string
  /**
   * Image object used by LuxKit media (`id` and/or fully qualified `url`).
   */
  image?: App.Image
  alt?: string
  /**
   * @default lazy
   */
  loading?: 'eager' | 'lazy'
}

function Drop(props: Props) {
  const {
    className,
    position = 'top-left',
    size = DEFAULT_SIZE,
    id,
    image,
    alt,
    loading = 'lazy',
  } = props

  const reactId = React.useId()
  const clipPathId = `drop-clip-${reactId.replaceAll(':', '')}`
  const imageId = id || image?.id
  const imageAlt = alt ?? image?.title ?? ''

  let imageElement: React.ReactNode
  if (imageId || image?.url) {
    imageElement = <Image
      id={imageId}
      image={imageId ? undefined : image}
      alt={imageAlt}
      fit="center"
      aspectRatio="1"
      objectFit="cover"
      width={size}
      height={size}
      loading={loading}
    />
  }

  return <DropShell
    className={className}
    style={{ '--drop-size': rem(size) } as React.CSSProperties}
  >
    <ClipSvg
      width={0}
      height={0}
      aria-hidden="true"
      focusable="false"
    >
      <clipPath
        id={clipPathId}
        clipPathUnits="objectBoundingBox"
      >
        <path d={DROP_CLIP_PATHS[position]} />
      </clipPath>
    </ClipSvg>
    <DropRoot
      style={{ '--drop-clip-path': `url(#${clipPathId})` } as React.CSSProperties}
    >
      {imageElement}
    </DropRoot>
  </DropShell>
}

export default Drop
